// frontend/src/components/RemediationFindingCard.jsx
import React, { useEffect, useMemo, useState } from "react";

/**
 * RemediationFindingCard
 *
 * Props used by RemediationHub:
 * - finding
 * - entry
 * - statuses
 * - mode: "EXECUTIVE" | "LEGAL"
 * - onStatusChange, onNotesChange
 * - onEvidenceAdd, onEvidenceChange, onEvidenceRemove
 * - onUploadAdd, onUploadRemove
 * - applicableJurisdictions: array of regime keys (from results.summary.jurisdictions)
 *
 * Layout:
 * - layoutVariant: "ROW_C_FULL_PLAYBOOK" (Row A/B/C/D layout)
 * - expandAllTick / collapseAllTick: hub-level signals to open/close Playbook
 *
 * Legal mode:
 * - Playbook stays the same.
 * - Adds Legal Details accordion beneath (bold CTA).
 * - defaultExpandLegalDetails: hub decides (top 1–3 gravity items).
 *
 * © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
 */

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function safeObj(x) {
  return x && typeof x === "object" && !Array.isArray(x) ? x : {};
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function statusToLabel(status) {
  const s = String(status || "OPEN").toUpperCase();
  if (s === "CLOSED") return "Closed";
  if (s === "MITIGATED") return "Mitigated";
  if (s === "IN_PROGRESS") return "In Progress";
  return "Open";
}

function kindToPill(kind) {
  const k = String(kind || "").toUpperCase();
  if (k === "UNSURE") return { label: "Unsure", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "Failed", cls: "bg-red-50 text-red-700 border-red-200" };
}

function categoryPill(category) {
  const c = String(category || "—");
  return { label: c, cls: "bg-slate-50 text-slate-700 border-slate-200" };
}

function gravityPill(gravity) {
  const g = Number(gravity || 0);
  const txt = g ? `Gravity ${g}/10` : "Gravity —";
  return { label: txt, cls: "bg-slate-50 text-slate-700 border-slate-200" };
}

function buildFindingQuestionText(finding) {
  const t = String(finding?.text || "").trim();
  if (t) return t;
  const title = String(finding?.title || "").trim();
  return title || "—";
}

function normalizeStatus(s) {
  const v = String(s || "OPEN").toUpperCase();
  if (v === "OPEN" || v === "IN_PROGRESS" || v === "MITIGATED" || v === "CLOSED") return v;
  return "OPEN";
}

function chevronDownSvg(cls = "h-4 w-4") {
  return (
    <svg className={cls} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function chevronUpSvg(cls = "h-4 w-4") {
  return (
    <svg className={cls} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M14.77 12.79a.75.75 0 0 1-1.06-.02L10 9.06l-3.71 3.71a.75.75 0 1 1-1.06-1.06l4.24-4.24a.75.75 0 0 1 1.06 0l4.24 4.24a.75.75 0 0 1-.02 1.08Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function normalizeRegimes(regimes) {
  // Deterministic: no "ALL" guessing. If absent, show an explicit placeholder.
  const list = safeArray(regimes).map(String).map((s) => s.trim()).filter(Boolean);
  const uniq = Array.from(new Set(list));
  return uniq.length ? uniq : ["NOT_SPECIFIED"];
}

function regimeLabel(key) {
  const k = String(key || "").toUpperCase();
  if (k === "NOT_SPECIFIED") return "Not specified";
  if (k === "US_FED") return "US (Federal)";
  if (k === "US_STATE") return "US (State)";
  return k;
}

function buildLegalDetailsModel({ finding, regimes }) {
  const f = safeObj(finding);
  const category = String(f?.category || "—");
  const title = String(f?.title || "—");

  // Lightweight “legal-mode” scaffolding — no statutory citations (MVP-safe).
  return {
    header: `Legal Details — ${title}`,
    regimes: normalizeRegimes(regimes),
    enforcementLens: (() => {
      const c = category.toLowerCase();
      if (c.includes("prohibited"))
        return "Potential prohibited-use classification; prioritize immediate containment and documented cessation.";
      if (c.includes("high-risk"))
        return "Higher oversight expectations; emphasize governance file, DPIA/impact assessment, and operational controls.";
      if (c.includes("governance"))
        return "Audit-trail exposure; emphasize documentation completeness, approvals, and monitoring logs.";
      if (c.includes("transparency"))
        return "User rights and disclosure exposure; emphasize notices, opt-outs, and complaint pathways.";
      return "General compliance exposure; apply standard defensibility controls.";
    })(),
    checklist: [
      "Document factual system behavior and scope (what, where, who affected).",
      "Capture change-control evidence (tickets, approvals, release notes).",
      "Preserve monitoring or testing artifacts (evaluations, drift, QA samples).",
      "Record remediation decision + owner + date + verification method.",
      "Store defensibility evidence (policies, training, notices, screenshots, logs).",
    ],
    evidenceFocus: [
      "Policy / SOP reference (versioned)",
      "Owner attestation (role + date)",
      "Test results or monitoring screenshot",
      "User-facing disclosure / UX proof",
      "Vendor contract clause or DPA excerpt (if applicable)",
    ],
    note:
      "This section is a structured legal-mode checklist (no statutory citations yet). Citations and precedent mapping can be layered later (Legal tier).",
  };
}

export default function RemediationFindingCard({
  finding,
  entry,
  statuses,
  mode,

  onStatusChange,
  onNotesChange,
  onEvidenceAdd,
  onEvidenceChange,
  onEvidenceRemove,
  onUploadAdd,
  onUploadRemove,

  // from hub (optional)
  applicableJurisdictions = [],

  layoutVariant = "ROW_C_FULL_PLAYBOOK",
  expandAllTick = 0,
  collapseAllTick = 0,

  defaultExpandLegalDetails = false,
}) {
  const f = safeObj(finding);
  const e = safeObj(entry);

  const fid = String(f?.id ?? "");
  const questionText = buildFindingQuestionText(f);

  const pillKind = kindToPill(f?.kind);
  const pillCategory = categoryPill(f?.category);
  const pillGravity = gravityPill(f?.gravity);

  const statusValue = normalizeStatus(e?.status || "OPEN");

  const [playbookOpen, setPlaybookOpen] = useState(true);
  const [legalOpen, setLegalOpen] = useState(false);

  // Hub-level expand/collapse (playbook)
  useEffect(() => {
    if (expandAllTick) setPlaybookOpen(true);
  }, [expandAllTick]);

  useEffect(() => {
    if (collapseAllTick) setPlaybookOpen(false);
  }, [collapseAllTick]);

  // Default-expand Legal Details for top 1–3 (Legal mode only)
  useEffect(() => {
    if (String(mode || "EXECUTIVE").toUpperCase() !== "LEGAL") {
      setLegalOpen(false);
      return;
    }
    if (defaultExpandLegalDetails) setLegalOpen(true);
  }, [mode, defaultExpandLegalDetails]);

  // ✅ IMPORTANT FIX:
  // If hub does not pass applicableJurisdictions, fall back to finding.jurisdictions
  const effectiveRegimes = useMemo(() => {
    const hub = safeArray(applicableJurisdictions);
    if (hub.length) return hub;
    const fromFinding = safeArray(f?.jurisdictions);
    return fromFinding;
  }, [applicableJurisdictions, f]);

  const legalModel = useMemo(
    () => buildLegalDetailsModel({ finding: f, regimes: effectiveRegimes }),
    [f, effectiveRegimes]
  );

  const evidenceLinks = safeArray(e?.evidence_links);
  const uploads = safeArray(e?.uploads);

  const playbookText = String(f?.guidance || "").trim();
  const showRapidActions = String(mode || "EXECUTIVE").toUpperCase() === "EXECUTIVE";

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      {/* Row A: Title + pills */}
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Finding</div>
            <div className="mt-2 text-sm font-black text-slate-900 whitespace-pre-line">{questionText}</div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
            <div className={cn("rounded-full border px-3 py-1.5 text-xs font-black uppercase tracking-widest", pillKind.cls)}>
              {pillKind.label}
            </div>
            <div className={cn("rounded-full border px-3 py-1.5 text-xs font-black uppercase tracking-widest", pillCategory.cls)}>
              {pillCategory.label}
            </div>
            <div className={cn("rounded-full border px-3 py-1.5 text-xs font-black uppercase tracking-widest", pillGravity.cls)}>
              {pillGravity.label}
            </div>
          </div>
        </div>

        {/* Row B: Status + Playbook toggle */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Status</div>

            <div className="mt-3 flex flex-col gap-3">
              <select
                value={statusValue}
                onChange={(ev) => onStatusChange?.(fid, ev.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-800"
              >
                {safeArray(statuses).map((s) => (
                  <option key={String(s?.value)} value={String(s?.value)}>
                    {String(s?.label)}
                  </option>
                ))}
              </select>

              <div className="text-xs text-slate-500">
                Current status: <span className="font-black text-slate-800">{statusToLabel(statusValue)}</span>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                  How to Fix (Playbook)
                </div>
                <div className="mt-2 text-xs text-slate-500">Action guidance for this finding.</div>
              </div>

              <button
                type="button"
                onClick={() => setPlaybookOpen((v) => !v)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-xs font-black uppercase tracking-widest",
                  playbookOpen
                    ? "bg-slate-900 border-slate-900 text-white"
                    : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                )}
                title={playbookOpen ? "Collapse Playbook" : "Expand Playbook"}
              >
                {playbookOpen ? "Hide" : "Show"}
                {playbookOpen ? chevronUpSvg("h-4 w-4") : chevronDownSvg("h-4 w-4")}
              </button>
            </div>
          </div>
        </div>

        {/* Row C: Playbook + Legal accordion */}
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
          {playbookOpen ? (
            <>
              {showRapidActions ? (
                <div className="mb-4 rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
                  <div className="text-[11px] font-black uppercase tracking-[0.35em] text-emerald-900">
                    Rapid Remediation Actions
                  </div>
                  <div className="mt-2 text-sm text-emerald-950">
                    Prioritize high gravity items. Close what you can quickly to reduce remaining exposure for board-level reporting.
                  </div>
                </div>
              ) : null}

              <div className="text-sm text-slate-800 whitespace-pre-line">
                {playbookText || "No playbook guidance is available for this finding yet."}
              </div>

              {String(mode || "EXECUTIVE").toUpperCase() === "LEGAL" ? (
                <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-5">
                  <button
                    type="button"
                    onClick={() => setLegalOpen((v) => !v)}
                    className={cn(
                      "w-full rounded-2xl border px-4 py-3 text-left",
                      legalOpen
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 bg-white text-slate-900 hover:border-slate-400"
                    )}
                    title={legalOpen ? "Collapse Legal Details" : "Expand Legal Details"}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-black uppercase tracking-widest">Legal Details</div>
                        <div className="text-[11px] font-extrabold uppercase tracking-[0.3em] opacity-90">(Audit-Ready)</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs font-black uppercase tracking-widest">{legalOpen ? "Hide" : "Show"}</div>
                        {legalOpen ? chevronUpSvg("h-5 w-5") : chevronDownSvg("h-5 w-5")}
                      </div>
                    </div>
                  </button>

                  {legalOpen ? (
                    <div className="mt-4 space-y-4">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                          Applicable Regimes
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {safeArray(legalModel.regimes).map((r) => (
                            <div
                              key={String(r)}
                              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-black uppercase tracking-widest text-slate-700"
                              title={String(r)}
                            >
                              {regimeLabel(r)}
                            </div>
                          ))}
                        </div>
                        {safeArray(legalModel.regimes).includes("NOT_SPECIFIED") ? (
                          <div className="mt-2 text-xs text-slate-500">
                            No jurisdictions were present for this finding (neither hub jurisdictions nor finding.jurisdictions).
                          </div>
                        ) : null}
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                          Enforcement Lens
                        </div>
                        <div className="mt-2 text-sm text-slate-800">{legalModel.enforcementLens}</div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                            Documentation Checklist
                          </div>
                          <ul className="mt-2 list-disc pl-5 text-sm text-slate-800 space-y-1">
                            {safeArray(legalModel.checklist).map((x, idx) => (
                              <li key={idx}>{String(x)}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                            Evidence Focus
                          </div>
                          <ul className="mt-2 list-disc pl-5 text-sm text-slate-800 space-y-1">
                            {safeArray(legalModel.evidenceFocus).map((x, idx) => (
                              <li key={idx}>{String(x)}</li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="text-xs text-slate-500">{legalModel.note}</div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-sm text-slate-600">
              Playbook is collapsed. Use <span className="font-black">Show</span> to expand.
            </div>
          )}
        </div>

        {/* Row D: Notes, Evidence Links, Uploads */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 lg:col-span-1">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Notes</div>
            <textarea
              value={String(e?.notes || "")}
              onChange={(ev) => onNotesChange?.(fid, ev.target.value)}
              className="mt-3 h-28 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-slate-400"
              placeholder="Add internal notes, owner, target date, validation plan…"
            />
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 lg:col-span-1">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Evidence Links</div>
              <button
                type="button"
                onClick={() => onEvidenceAdd?.(fid)}
                className="rounded-xl bg-slate-900 px-3 py-2 text-xs font-black uppercase tracking-widest text-white"
              >
                Add
              </button>
            </div>

            <div className="mt-3 space-y-3">
              {evidenceLinks.length === 0 ? (
                <div className="text-sm text-slate-500">No evidence links yet.</div>
              ) : (
                evidenceLinks.map((row, idx) => {
                  const rid = String(row?.id || `e_${idx}`);
                  return (
                    <div key={rid} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="grid grid-cols-1 gap-2">
                        <input
                          value={String(row?.label || "")}
                          onChange={(ev) => onEvidenceChange?.(fid, rid, { ...row, id: rid, label: ev.target.value })}
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                          placeholder="Label (e.g., Policy link, Ticket, Screenshot)"
                        />
                        <input
                          value={String(row?.url || "")}
                          onChange={(ev) => onEvidenceChange?.(fid, rid, { ...row, id: rid, url: ev.target.value })}
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                          placeholder="URL"
                        />
                        <textarea
                          value={String(row?.notes || "")}
                          onChange={(ev) => onEvidenceChange?.(fid, rid, { ...row, id: rid, notes: ev.target.value })}
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                          placeholder="Notes"
                        />
                        <button
                          type="button"
                          onClick={() => onEvidenceRemove?.(fid, rid)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-slate-300"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 lg:col-span-1">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Uploads</div>
              <button
                type="button"
                onClick={() =>
                  onUploadAdd?.(fid, {
                    id: `up_${Date.now()}_${Math.random().toString(16).slice(2)}`,
                    filename: "example.pdf",
                    mime_type: "application/pdf",
                    size_bytes: 0,
                    last_modified_ms: Date.now(),
                    storage_state: "upload_ready",
                    storage_key: null,
                  })
                }
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-slate-300"
                title="Adds upload-ready metadata (demo placeholder)"
              >
                Add Upload (Meta)
              </button>
            </div>

            <div className="mt-3 space-y-3">
              {uploads.length === 0 ? (
                <div className="text-sm text-slate-500">No uploads yet.</div>
              ) : (
                uploads.map((u, idx) => {
                  const uid = String(u?.id || `u_${idx}`);
                  return (
                    <div key={uid} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="text-sm font-black text-slate-900">{String(u?.filename || "File")}</div>
                      <div className="mt-1 text-xs text-slate-500">
                        {String(u?.mime_type || "—")} • {Number(u?.size_bytes || 0).toLocaleString("en-US")} bytes
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Storage: <span className="font-black">{String(u?.storage_state || "—")}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => onUploadRemove?.(fid, uid)}
                        className="mt-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-slate-300"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Footer requirement */}
        <div className="mt-6 text-xs text-slate-400">© 2026 Advanced Advisory Services, LLC. All Rights Reserved.</div>
      </div>
    </div>
  );
}